import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Space, Tag, Tooltip, Popconfirm, Form, Input, Switch, Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CBModal from '../../components/CBModal';
import CBInputNumber from '../../components/CBInputNumber';
import { can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import { tauxRetenueApi, chargerTauxRetenue } from '../../services/tauxRetenueService';
import { GREEN } from './commun';

/* POURQUOI CE FICHIER EXISTE.

   Le referentiel des taux de retenue vivait dans un onglet de la page Fiscalite,
   a cote de l'etat « Retenues sur honoraires a reverser ». Les deux n'ont pourtant
   pas la meme nature : l'un est un etat qu'on consulte, l'autre un parametrage
   qu'on modifie. Les taux ne concernent que les honoraires d'experts, et leur
   place est donc aupres des experts, dans Parametres.

   Le composant est sorti ici inchange pour que les deux pages puissent le monter
   sans le dupliquer, et pour qu'un futur deplacement ne soit plus une chirurgie
   dans un fichier de 345 lignes. */


/**
 * Taux de retenue à la source appliqués aux honoraires d'experts.
 *
 * Modifier un taux vaut pour les règlements À VENIR : chaque lot a gardé le taux
 * appliqué le jour de sa création. Un changement de taux légal se fait donc d'un
 * seul geste, sans réaffecter les experts ni réécrire l'historique.
 */
export default function TauxDeRetenue() {
  const { t } = useTranslation('comptabilite');
  const [taux, setTaux] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const modifiable = can(HAB.PARAM_COMPTABLE_UPDATE);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      setTaux(await tauxRetenueApi.lister());
    } catch (e) {
      toast.error(e?.response?.data?.message || t('fiscalite.messages.chargementTauxImpossible'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  // Après toute modification : la liste de cet écran, ET la liste partagée par les
  // fiches expert et les lots de règlement déjà ouverts.
  const apresModification = () => { charger(); chargerTauxRetenue(true).catch(() => {}); };

  const ouvrir = (record) => {
    setEditing(record || null);
    form.resetFields();
    form.setFieldsValue(record
      ? { pourcentage: Number(record.pourcentage), libelle: record.libelle, actif: record.actif }
      : { actif: true });
    setModalOpen(true);
  };

  const enregistrer = async (vals) => {
    try {
      if (editing) await tauxRetenueApi.modifier(editing.id, vals);
      else await tauxRetenueApi.creer(vals);
      toast.success(editing ? t('fiscalite.messages.tauxModifie') : t('fiscalite.messages.tauxCree'));
      setModalOpen(false);
      apresModification();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.enregistrementImpossible'));
    }
  };

  const supprimer = async (record) => {
    try {
      await tauxRetenueApi.supprimer(record.id);
      toast.success(t('fiscalite.messages.tauxSupprime'));
      apresModification();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.suppressionImpossible'));
    }
  };

  const colonnes = [
    { title: t('fiscalite.colonnes.taux'), dataIndex: 'pourcentage', width: 110,
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
          {r.affichage.split(' — ')[0]}
        </span>
      ) },
    { title: t('fiscalite.colonnes.designation'), dataIndex: 'libelle',
      render: (v) => v || <span style={{ color: '#9ca3af' }}>—</span> },
    { title: t('fiscalite.colonnes.expertsConcernes'), dataIndex: 'nombreExperts', width: 160, align: 'right',
      render: (v) => (v ? t('fiscalite.nbExperts', { count: v }) : <span style={{ color: '#9ca3af' }}>{t('fiscalite.aucun')}</span>) },
    { title: t('fiscalite.colonnes.lotsReglement'), dataIndex: 'nombreLots', width: 160, align: 'right',
      render: (v) => (v ? t('fiscalite.nbLots', { count: v }) : <span style={{ color: '#9ca3af' }}>{t('fiscalite.aucun')}</span>) },
    { title: t('commun.statut'), dataIndex: 'actif', width: 110, align: 'center',
      render: (v) => (v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('fiscalite.enVigueur')}</Tag>
        : <Tag style={{ borderRadius: 20 }}>{t('fiscalite.desactive')}</Tag>) },
  ];
  if (modifiable) {
    colonnes.push({
      title: t('commun.actions'), key: 'actions', width: 170, align: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" icon={<EditOutlined />} onClick={() => ouvrir(r)}
            style={{ borderColor: GREEN, color: GREEN, borderRadius: 6 }}>
            {t('commun.modifier')}
          </Button>
          {r.nombreExperts > 0 || r.nombreLots > 0 ? (
            <Tooltip title={t('fiscalite.suppressionVerrouillee')}>
              <Button size="small" icon={<DeleteOutlined />} disabled style={{ borderRadius: 6 }} />
            </Tooltip>
          ) : (
            <Popconfirm title={t('fiscalite.confirmerSuppression')} onConfirm={() => supprimer(r)} okText={t('commun.oui')} cancelText={t('commun.non')}>
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  // Désactiver un taux que portent des experts est refusé par le serveur : on le
  // signale dès la saisie plutôt qu'au clic sur Enregistrer.
  const actifSaisi = Form.useWatch('actif', form);
  const desactivationBloquee = editing && editing.actif && actifSaisi === false && editing.nombreExperts > 0;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ color: '#667085', fontSize: 13 }}>
          {t('fiscalite.aideTaux')}
        </span>
        {modifiable && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => ouvrir(null)}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
            {t('fiscalite.nouveauTaux')}
          </Button>
        )}
      </div>

      <Table className="cb-table" dataSource={taux} columns={colonnes} rowKey="id" loading={loading}
        pagination={false} />

      <CBModal title={editing ? t('fiscalite.modale.titreModifier', { taux: editing.affichage.split(' — ')[0] }) : t('fiscalite.modale.titreNouveau')}
        open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={520} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer} style={{ marginTop: 16 }}>
          <Form.Item name="pourcentage" label={t('fiscalite.colonnes.taux')}
            rules={[{ required: true, message: t('fiscalite.modale.tauxRequis') }]}
            extra={editing?.nombreLots
              ? t('fiscalite.modale.lotsDejaRegles', { nombre: editing.nombreLots })
              : undefined}>
            <CBInputNumber min={0} max={99.99} step={0.5} precision={2} addonAfter="%" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="libelle" label={t('fiscalite.modale.designationFacultatif')}>
            <Input maxLength={120} placeholder={t('fiscalite.modale.placeholderDesignation')} />
          </Form.Item>
          <Form.Item name="actif" label={t('fiscalite.enVigueur')} valuePropName="checked"
            extra={t('fiscalite.modale.aideEnVigueur')}>
            <Switch checkedChildren={t('commun.oui')} unCheckedChildren={t('commun.non')} />
          </Form.Item>
          {desactivationBloquee && (
            <Alert type="warning" showIcon style={{ marginBottom: 12, borderRadius: 8 }}
              message={t('fiscalite.modale.desactivationBloquee', { nombre: editing.nombreExperts })} />
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('commun.annuler')}</Button>
            <Button type="primary" htmlType="submit" disabled={desactivationBloquee}
              style={desactivationBloquee ? { borderRadius: 8 } : { background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {editing ? t('commun.enregistrer') : t('fiscalite.modale.creerTaux')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </>
  );
}

